import {
  LoginCredentials,
  LoginResponse,
  User,
  ApiResponse,
  PaginatedResponse,
  PaginatedApiResponse,
  Job,
  CVUpload,
  Package,
  ConfirmEmailProfile,
} from "../types";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000/api/v1";
const BASE_URL = API_URL.replace("/api/v1", ""); // Backend base URL for media files

// Helper function to get full media URL
export const getMediaUrl = (path: string | null): string | null => {
  if (!path) return null;
  // If path already starts with http, return as is
  if (path.startsWith("http")) return path;
  // Otherwise prepend base URL
  return `${BASE_URL}${path}`;
};

class ApiService {
  private isRefreshing = false;
  private refreshSubscribers: ((token: string) => void)[] = [];

  private getHeaders(includeAuth: boolean = false): HeadersInit {
    const headers: HeadersInit = {
      "Content-Type": "application/json",
    };

    if (includeAuth) {
      const token = localStorage.getItem("accessToken");
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
    }

    return headers;
  }

  private subscribeTokenRefresh(cb: (token: string) => void) {
    this.refreshSubscribers.push(cb);
  }

  private onTokenRefreshed(token: string) {
    this.refreshSubscribers.forEach((cb) => cb(token));
    this.refreshSubscribers = [];
  }

  private handleSessionExpired() {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("user");

    if (!window.location.pathname.includes("/login")) {
      window.location.href = "/login";
    }
  }

  private async refreshAccessToken(): Promise<string | null> {
    const refreshToken = localStorage.getItem("refreshToken");

    if (!refreshToken) {
      console.log("🔒 No refresh token - session expired");
      this.handleSessionExpired();
      return null;
    }

    try {
      const response = await fetch(`${API_URL}/auth/token/refresh/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh: refreshToken }),
      });

      if (!response.ok) {
        console.log("❌ Refresh token expired - session timeout");
        this.handleSessionExpired();
        return null;
      }

      const data = await response.json();
      const newAccessToken = data.data?.access || data.access;
      const newRefreshToken = data.data?.refresh || data.refresh;

      if (newAccessToken) {
        localStorage.setItem("accessToken", newAccessToken);
        if (newRefreshToken) {
          localStorage.setItem("refreshToken", newRefreshToken);
        }
        console.log("✅ Token refreshed successfully - admin stays logged in");
        return newAccessToken;
      }

      this.handleSessionExpired();
      return null;
    } catch (error) {
      console.error("Token refresh error:", error);
      this.handleSessionExpired();
      return null;
    }
  }

  private async makeRequest<T>(
    url: string,
    options: RequestInit,
    includeAuth: boolean = false
  ): Promise<T> {
    let response = await fetch(url, options);

    // If 401 and includeAuth is true, try to refresh token
    if (response.status === 401 && includeAuth) {
      if (!this.isRefreshing) {
        this.isRefreshing = true;
        const newToken = await this.refreshAccessToken();
        this.isRefreshing = false;

        if (newToken) {
          const headers = options.headers as Record<string, string>;
          headers["Authorization"] = `Bearer ${newToken}`;
          this.onTokenRefreshed(newToken);
          response = await fetch(url, { ...options, headers });
        }
      } else {
        // Wait for token refresh to complete
        const newToken = await new Promise<string>((resolve) => {
          this.subscribeTokenRefresh((token) => resolve(token));
        });

        const headers = options.headers as Record<string, string>;
        headers["Authorization"] = `Bearer ${newToken}`;
        response = await fetch(url, { ...options, headers });
      }
    }

    return this.handleResponse<T>(response);
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      const error = await response
        .json()
        .catch(() => ({ message: "Request failed" }));
      throw new Error(error.message || `HTTP ${response.status}`);
    }
    return response.json();
  }

  // Authentication
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    const response = await fetch(`${API_URL}/auth/login/`, {
      method: "POST",
      headers: this.getHeaders(),
      body: JSON.stringify(credentials),
    });
    const data = await this.handleResponse<LoginResponse>(response);

    // Store tokens - Updated to match backend response structure
    if (data.success && data.data && data.data.tokens) {
      localStorage.setItem("accessToken", data.data.tokens.access);
      localStorage.setItem("refreshToken", data.data.tokens.refresh);
      localStorage.setItem("user", JSON.stringify(data.data.user));
    }

    return data;
  }

  async getProfile(): Promise<ApiResponse<User>> {
    return this.makeRequest<ApiResponse<User>>(
      `${API_URL}/me/`,
      { headers: this.getHeaders(true) },
      true
    );
  }

  logout() {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("user");
  }

  // Users Management
  async getUsers(
    params: {
      page?: number;
      page_size?: number;
      search?: string;
      role?: string;
      status?: string;
      order_by?: string;
      promo_code?: string;
      verified?: string;
    } = {}
  ): Promise<PaginatedApiResponse<User>> {
    const queryParams = new URLSearchParams();
    queryParams.append("page", String(params.page || 1));
    queryParams.append("page_size", String(params.page_size || 10));
    if (params.search) queryParams.append("search", params.search);
    if (params.role && params.role !== "all")
      queryParams.append("role", params.role);
    if (params.status && params.status !== "all")
      queryParams.append("status", params.status);
    if (params.promo_code && params.promo_code !== "all")
      queryParams.append("promo_code", params.promo_code);
    if (params.verified && params.verified !== "all")
      queryParams.append("verified", params.verified);
    if (params.order_by) queryParams.append("order_by", params.order_by);

    return this.makeRequest<PaginatedApiResponse<User>>(
      `${API_URL}/users/?${queryParams.toString()}`,
      { headers: this.getHeaders(true) },
      true
    );
  }

  async getUser(id: number): Promise<ApiResponse<User>> {
    return this.makeRequest<ApiResponse<User>>(
      `${API_URL}/users/${id}/`,
      { headers: this.getHeaders(true) },
      true
    );
  }

  async updateUser(
    id: number,
    data: Partial<User>
  ): Promise<ApiResponse<User>> {
    return this.makeRequest<ApiResponse<User>>(
      `${API_URL}/users/${id}/update/`,
      {
        method: "PUT",
        headers: this.getHeaders(true),
        body: JSON.stringify(data),
      },
      true
    );
  }

  async deleteUser(id: number): Promise<ApiResponse<any>> {
    return this.makeRequest<ApiResponse<any>>(
      `${API_URL}/users/${id}/delete/`,
      {
        method: "DELETE",
        headers: this.getHeaders(true),
      },
      true
    );
  }

  async enableDisableUser(
    id: number,
    enabled: boolean
  ): Promise<ApiResponse<User>> {
    const response = await fetch(`${API_URL}/users/${id}/toggle-status/`, {
      method: "POST",
      headers: this.getHeaders(true),
      body: JSON.stringify({ is_enabled: enabled }),
    });
    return this.handleResponse<ApiResponse<User>>(response);
  }

  async confirmUserEmail(
    id: number,
    profile?: ConfirmEmailProfile
  ): Promise<ApiResponse<User>> {
    const response = await fetch(`${API_URL}/users/${id}/confirm-email/`, {
      method: "POST",
      headers: this.getHeaders(true),
      body: JSON.stringify(
        profile ? { set_profile: true, ...profile } : { set_profile: false }
      ),
    });
    return this.handleResponse<ApiResponse<User>>(response);
  }

  async getUserStats(id: number): Promise<ApiResponse<any>> {
    const response = await fetch(`${API_URL}/users/${id}/stats/`, {
      headers: this.getHeaders(true),
    });
    return this.handleResponse<ApiResponse<any>>(response);
  }

  async impersonateUser(userId: number): Promise<ApiResponse<any>> {
    const response = await fetch(`${API_URL}/users/${userId}/impersonate/`, {
      method: "POST",
      headers: this.getHeaders(true),
    });
    return this.handleResponse<ApiResponse<any>>(response);
  }

  // Jobs Management
  async getJobs(page: number = 1): Promise<PaginatedApiResponse<Job>> {
    return this.makeRequest<PaginatedApiResponse<Job>>(
      `${API_URL}/jobs/admin/all/?page=${page}&page_size=100`,
      { headers: this.getHeaders(true) },
      true
    );
  }

  async getJob(id: number): Promise<ApiResponse<Job>> {
    const response = await fetch(`${API_URL}/jobs/admin/${id}/`, {
      headers: this.getHeaders(true),
    });
    return this.handleResponse<ApiResponse<Job>>(response);
  }

  async getJobCVs(
    jobId: number,
    page: number = 1
  ): Promise<PaginatedApiResponse<CVUpload>> {
    const response = await fetch(
      `${API_URL}/jobs/admin/${jobId}/cvs/?page=${page}&page_size=100`,
      {
        headers: this.getHeaders(true),
      }
    );
    return this.handleResponse<PaginatedApiResponse<CVUpload>>(response);
  }

  async deleteJob(id: number): Promise<ApiResponse<any>> {
    const response = await fetch(`${API_URL}/jobs/admin/${id}/delete/`, {
      method: "DELETE",
      headers: this.getHeaders(true),
    });
    return this.handleResponse<ApiResponse<any>>(response);
  }

  async getJobStats(): Promise<ApiResponse<any>> {
    const response = await fetch(`${API_URL}/jobs/admin/stats/`, {
      headers: this.getHeaders(true),
    });
    return this.handleResponse<ApiResponse<any>>(response);
  }

  // Package Management (Admin)
  async getPackages(): Promise<ApiResponse<Package[]>> {
    const response = await fetch(`${API_URL}/admin/packages/`, {
      headers: this.getHeaders(true),
    });
    return this.handleResponse<ApiResponse<Package[]>>(response);
  }

  async getPublicPackages(): Promise<ApiResponse<Package[]>> {
    const response = await fetch(`${API_URL}/public/packages/`, {
      method: "GET",
    });
    return this.handleResponse<ApiResponse<Package[]>>(response);
  }

  async getPackageStats(): Promise<ApiResponse<any>> {
    const response = await fetch(`${API_URL}/packages/stats/`, {
      headers: this.getHeaders(true),
    });
    return this.handleResponse<ApiResponse<any>>(response);
  }

  async getDashboardStats(): Promise<ApiResponse<any>> {
    return this.makeRequest<ApiResponse<any>>(
      `${API_URL}/dashboard/stats/`,
      { headers: this.getHeaders(true) },
      true
    );
  }

  // Usage History endpoints
  async getUsageHistory(params?: {
    page?: number;
    page_size?: number;
    user_id?: number;
    job_id?: number;
    action_type?: string;
    start_date?: string;
    end_date?: string;
    search?: string;
  }): Promise<ApiResponse<any>> {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
    }
    const url = `${API_URL}/usage-history/${
      queryParams.toString() ? "?" + queryParams.toString() : ""
    }`;
    const response = await fetch(url, {
      headers: this.getHeaders(true),
    });
    return this.handleResponse<ApiResponse<any>>(response);
  }

  async getUsageHistoryDetail(id: number): Promise<ApiResponse<any>> {
    const response = await fetch(`${API_URL}/usage-history/${id}/`, {
      headers: this.getHeaders(true),
    });
    return this.handleResponse<ApiResponse<any>>(response);
  }

  // Analysis Runs endpoints
  async getAnalysisRuns(params?: {
    page?: number;
    page_size?: number;
    user_id?: number;
    job_id?: number;
    status?: string;
    run_type?: string;
    start_date?: string;
    end_date?: string;
    search?: string;
  }): Promise<ApiResponse<any>> {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
    }
    const url = `${API_URL}/analysis-runs/${
      queryParams.toString() ? "?" + queryParams.toString() : ""
    }`;
    const response = await fetch(url, {
      headers: this.getHeaders(true),
    });
    return this.handleResponse<ApiResponse<any>>(response);
  }

  async getAnalysisRunDetail(id: number): Promise<ApiResponse<any>> {
    const response = await fetch(`${API_URL}/analysis-runs/${id}/`, {
      headers: this.getHeaders(true),
    });
    return this.handleResponse<ApiResponse<any>>(response);
  }

  async getPackage(id: number): Promise<ApiResponse<Package>> {
    const response = await fetch(`${API_URL}/admin/packages/${id}/`, {
      headers: this.getHeaders(true),
    });
    return this.handleResponse<ApiResponse<Package>>(response);
  }

  async createPackage(
    data: Partial<Package> | FormData
  ): Promise<ApiResponse<Package>> {
    const isFormData = data instanceof FormData;
    const headers = isFormData
      ? { Authorization: `Bearer ${localStorage.getItem("accessToken")}` }
      : this.getHeaders(true);

    const response = await fetch(`${API_URL}/admin/packages/`, {
      method: "POST",
      headers,
      body: isFormData ? data : JSON.stringify(data),
    });
    return this.handleResponse<ApiResponse<Package>>(response);
  }

  async updatePackage(
    id: number,
    data: Partial<Package> | FormData
  ): Promise<ApiResponse<Package>> {
    const isFormData = data instanceof FormData;
    const headers = isFormData
      ? { Authorization: `Bearer ${localStorage.getItem("accessToken")}` }
      : this.getHeaders(true);

    const response = await fetch(`${API_URL}/admin/packages/${id}/`, {
      method: "PUT",
      headers,
      body: isFormData ? data : JSON.stringify(data),
    });
    return this.handleResponse<ApiResponse<Package>>(response);
  }

  async deletePackage(id: number): Promise<ApiResponse<any>> {
    const response = await fetch(`${API_URL}/admin/packages/${id}/`, {
      method: "DELETE",
      headers: this.getHeaders(true),
    });
    return this.handleResponse<ApiResponse<any>>(response);
  }
  
  // Feature Management (Admin)
  async getFeatures(): Promise<ApiResponse<any[]>> {
    const response = await fetch(`${API_URL}/admin/features/`, {
      headers: this.getHeaders(true),
    });
    return this.handleResponse<ApiResponse<any[]>>(response);
  }
  
  async createFeature(data: any): Promise<ApiResponse<any>> {
    const response = await fetch(`${API_URL}/admin/features/`, {
      method: "POST",
      headers: this.getHeaders(true),
      body: JSON.stringify(data),
    });
    return this.handleResponse<ApiResponse<any>>(response);
  }
  
  async updateFeature(id: number, data: any): Promise<ApiResponse<any>> {
    const response = await fetch(`${API_URL}/admin/features/${id}/`, {
      method: "PUT",
      headers: this.getHeaders(true),
      body: JSON.stringify(data),
    });
    return this.handleResponse<ApiResponse<any>>(response);
  }
  
  async deleteFeature(id: number): Promise<ApiResponse<any>> {
    const response = await fetch(`${API_URL}/admin/features/${id}/`, {
      method: "DELETE",
      headers: this.getHeaders(true),
    });
    return this.handleResponse<ApiResponse<any>>(response);
  }
  
  async bulkUpdateFeatureOrder(features: Array<{id: number, display_order: number}>): Promise<ApiResponse<any>> {
    const response = await fetch(`${API_URL}/admin/features/bulk-order-update/`, {
      method: "POST",
      headers: this.getHeaders(true),
      body: JSON.stringify({ features }),
    });
    return this.handleResponse<ApiResponse<any>>(response);
  }
  
  // Package Feature Management
  async getPackageFeatures(packageId: number): Promise<ApiResponse<any[]>> {
    const response = await fetch(`${API_URL}/admin/packages/${packageId}/features/`, {
      headers: this.getHeaders(true),
    });
    return this.handleResponse<ApiResponse<any[]>>(response);
  }
  
  async updatePackageFeature(packageId: number, featureId: number, value: any): Promise<ApiResponse<any>> {
    const response = await fetch(`${API_URL}/admin/packages/${packageId}/features/${featureId}/`, {
      method: "PUT",
      headers: this.getHeaders(true),
      body: JSON.stringify({ value }),
    });
    return this.handleResponse<ApiResponse<any>>(response);
  }
  
  async bulkUpdatePackageFeatures(packageId: number, features: Array<{feature_id: number, value: any}>): Promise<ApiResponse<any>> {
    const response = await fetch(`${API_URL}/admin/packages/${packageId}/features/bulk-update/`, {
      method: "POST",
      headers: this.getHeaders(true),
      body: JSON.stringify({ features }),
    });
    return this.handleResponse<ApiResponse<any>>(response);
  }

  async togglePackageStatus(id: number): Promise<ApiResponse<Package>> {
    const response = await fetch(`${API_URL}/packages/${id}/toggle-status/`, {
      method: "POST",
      headers: this.getHeaders(true),
    });
    return this.handleResponse<ApiResponse<Package>>(response);
  }

  // User Package Management
  async updateUserPackage(
    userId: number,
    packageId: number,
    resetUsage: boolean = false
  ): Promise<ApiResponse<any>> {
    const response = await fetch(`${API_URL}/users/${userId}/package/`, {
      method: "PUT",
      headers: this.getHeaders(true),
      body: JSON.stringify({
        package_id: packageId,
        reset_usage: resetUsage,
      }),
    });
    return this.handleResponse<ApiResponse<any>>(response);
  }

  async updateUserUsage(
    userId: number,
    data: {
      current_month_ai_usage?: number;
      total_ai_usage?: number;
      current_storage_usage?: number;
    }
  ): Promise<ApiResponse<any>> {
    const response = await fetch(`${API_URL}/users/${userId}/usage/`, {
      method: "PUT",
      headers: this.getHeaders(true),
      body: JSON.stringify(data),
    });
    return this.handleResponse<ApiResponse<any>>(response);
  }

  async updateUserLimits(
    userId: number,
    data: {
      custom_monthly_ai_limit?: number | null;
      custom_total_ai_limit?: number | null;
      custom_storage_limit?: number | null;
    }
  ): Promise<ApiResponse<any>> {
    const response = await fetch(`${API_URL}/users/${userId}/limits/`, {
      method: "PUT",
      headers: this.getHeaders(true),
      body: JSON.stringify(data),
    });
    return this.handleResponse<ApiResponse<any>>(response);
  }

  // ============================================
  // COUNTRIES AND INDUSTRIES MANAGEMENT
  // ============================================

  // Countries
  async getCountries(): Promise<ApiResponse<any>> {
    const response = await fetch(`${API_URL}/admin/countries/`, {
      method: "GET",
      headers: this.getHeaders(true),
    });
    return this.handleResponse<ApiResponse<any>>(response);
  }

  async getPublicCountries(): Promise<ApiResponse<any>> {
    const response = await fetch(`${API_URL}/public/countries/`, {
      method: "GET",
    });
    return this.handleResponse<ApiResponse<any>>(response);
  }

  async createCountry(data: {
    code: string;
    name: string;
    name_fr?: string;
    name_ar?: string;
  }): Promise<ApiResponse<any>> {
    const response = await fetch(`${API_URL}/admin/countries/`, {
      method: "POST",
      headers: this.getHeaders(true),
      body: JSON.stringify(data),
    });
    return this.handleResponse<ApiResponse<any>>(response);
  }

  async updateCountry(
    id: number,
    data: { code: string; name: string; name_fr?: string; name_ar?: string }
  ): Promise<ApiResponse<any>> {
    const response = await fetch(`${API_URL}/admin/countries/${id}/`, {
      method: "PUT",
      headers: this.getHeaders(true),
      body: JSON.stringify(data),
    });
    return this.handleResponse<ApiResponse<any>>(response);
  }

  async deleteCountry(id: number): Promise<ApiResponse<any>> {
    const response = await fetch(`${API_URL}/admin/countries/${id}/`, {
      method: "DELETE",
      headers: this.getHeaders(true),
    });
    return this.handleResponse<ApiResponse<any>>(response);
  }

  async toggleCountryStatus(id: number): Promise<ApiResponse<any>> {
    const response = await fetch(`${API_URL}/admin/countries/${id}/toggle/`, {
      method: "PATCH",
      headers: this.getHeaders(true),
    });
    return this.handleResponse<ApiResponse<any>>(response);
  }

  // Industries
  async getIndustries(): Promise<ApiResponse<any>> {
    const response = await fetch(`${API_URL}/admin/industries/`, {
      method: "GET",
      headers: this.getHeaders(true),
    });
    return this.handleResponse<ApiResponse<any>>(response);
  }

  async createIndustry(data: {
    name: string;
    name_fr?: string;
  }): Promise<ApiResponse<any>> {
    const response = await fetch(`${API_URL}/admin/industries/`, {
      method: "POST",
      headers: this.getHeaders(true),
      body: JSON.stringify(data),
    });
    return this.handleResponse<ApiResponse<any>>(response);
  }

  async updateIndustry(
    id: number,
    data: { name: string; name_fr?: string }
  ): Promise<ApiResponse<any>> {
    const response = await fetch(`${API_URL}/admin/industries/${id}/`, {
      method: "PUT",
      headers: this.getHeaders(true),
      body: JSON.stringify(data),
    });
    return this.handleResponse<ApiResponse<any>>(response);
  }

  async deleteIndustry(id: number): Promise<ApiResponse<any>> {
    const response = await fetch(`${API_URL}/admin/industries/${id}/`, {
      method: "DELETE",
      headers: this.getHeaders(true),
    });
    return this.handleResponse<ApiResponse<any>>(response);
  }

  async toggleIndustryStatus(id: number): Promise<ApiResponse<any>> {
    const response = await fetch(`${API_URL}/admin/industries/${id}/toggle/`, {
      method: "PATCH",
      headers: this.getHeaders(true),
    });
    return this.handleResponse<ApiResponse<any>>(response);
  }

  // ============================================
  // EMAIL MANAGEMENT
  // ============================================

  // SMTP Settings
  async getSMTPSettings(): Promise<ApiResponse<any>> {
    const response = await fetch(`${API_URL}/admin/smtp/`, {
      method: "GET",
      headers: this.getHeaders(true),
    });
    return this.handleResponse<ApiResponse<any>>(response);
  }

  async updateSMTPSettings(data: {
    host?: string;
    port?: number;
    username?: string;
    password?: string;
    use_tls?: boolean;
    use_ssl?: boolean;
    from_email?: string;
    from_name?: string;
    is_active?: boolean;
  }): Promise<ApiResponse<any>> {
    const response = await fetch(`${API_URL}/admin/smtp/`, {
      method: "PUT",
      headers: this.getHeaders(true),
      body: JSON.stringify(data),
    });
    return this.handleResponse<ApiResponse<any>>(response);
  }

  // Email Layouts
  async getEmailLayouts(): Promise<ApiResponse<any>> {
    const response = await fetch(`${API_URL}/admin/email-layouts/`, {
      method: "GET",
      headers: this.getHeaders(true),
    });
    return this.handleResponse<ApiResponse<any>>(response);
  }

  async getEmailLayout(id: number): Promise<ApiResponse<any>> {
    const response = await fetch(`${API_URL}/admin/email-layouts/${id}/`, {
      method: "GET",
      headers: this.getHeaders(true),
    });
    return this.handleResponse<ApiResponse<any>>(response);
  }

  async createEmailLayout(data: {
    name: string;
    description?: string;
    header_html: string;
    footer_html: string;
    default_styles?: string;
    is_default?: boolean;
    is_active?: boolean;
  }): Promise<ApiResponse<any>> {
    const response = await fetch(`${API_URL}/admin/email-layouts/`, {
      method: "POST",
      headers: this.getHeaders(true),
      body: JSON.stringify(data),
    });
    return this.handleResponse<ApiResponse<any>>(response);
  }

  async updateEmailLayout(
    id: number,
    data: {
      name?: string;
      description?: string;
      header_html?: string;
      footer_html?: string;
      default_styles?: string;
      is_default?: boolean;
      is_active?: boolean;
    }
  ): Promise<ApiResponse<any>> {
    const response = await fetch(`${API_URL}/admin/email-layouts/${id}/`, {
      method: "PUT",
      headers: this.getHeaders(true),
      body: JSON.stringify(data),
    });
    return this.handleResponse<ApiResponse<any>>(response);
  }

  async deleteEmailLayout(id: number): Promise<ApiResponse<any>> {
    const response = await fetch(`${API_URL}/admin/email-layouts/${id}/`, {
      method: "DELETE",
      headers: this.getHeaders(true),
    });
    return this.handleResponse<ApiResponse<any>>(response);
  }

  async setDefaultEmailLayout(id: number): Promise<ApiResponse<any>> {
    const response = await fetch(`${API_URL}/admin/email-layouts/${id}/set-default/`, {
      method: "POST",
      headers: this.getHeaders(true),
    });
    return this.handleResponse<ApiResponse<any>>(response);
  }

  // Email Templates
  async getEmailTemplates(): Promise<ApiResponse<any>> {
    const response = await fetch(`${API_URL}/admin/email-templates/`, {
      method: "GET",
      headers: this.getHeaders(true),
    });
    return this.handleResponse<ApiResponse<any>>(response);
  }

  async getEmailTemplate(id: number): Promise<ApiResponse<any>> {
    const response = await fetch(`${API_URL}/admin/email-templates/${id}/`, {
      method: "GET",
      headers: this.getHeaders(true),
    });
    return this.handleResponse<ApiResponse<any>>(response);
  }

  async createEmailTemplate(data: {
    name: string;
    template_type?: string;
    description?: string;
    subject_en: string;
    content_en: string;
    subject_fr?: string;
    content_fr?: string;
    from_email?: string;
    from_name?: string;
    cc?: string;
    bcc?: string;
    layout?: number;
    is_active?: boolean;
  }): Promise<ApiResponse<any>> {
    const response = await fetch(`${API_URL}/admin/email-templates/`, {
      method: "POST",
      headers: this.getHeaders(true),
      body: JSON.stringify(data),
    });
    return this.handleResponse<ApiResponse<any>>(response);
  }

  async updateEmailTemplate(
    id: number,
    data: {
      name?: string;
      template_type?: string;
      description?: string;
      subject_en?: string;
      content_en?: string;
      subject_fr?: string;
      content_fr?: string;
      from_email?: string;
      from_name?: string;
      cc?: string;
      bcc?: string;
      layout?: number | null;
      is_active?: boolean;
    }
  ): Promise<ApiResponse<any>> {
    const response = await fetch(`${API_URL}/admin/email-templates/${id}/`, {
      method: "PUT",
      headers: this.getHeaders(true),
      body: JSON.stringify(data),
    });
    return this.handleResponse<ApiResponse<any>>(response);
  }

  // Email History
  async getEmailHistory(
    page: number = 1,
    filters?: {
      status?: string;
      template_id?: number;
      to_email?: string;
    }
  ): Promise<ApiResponse<any>> {
    const queryParams = new URLSearchParams();
    queryParams.append("page", page.toString());
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
    }
    const response = await fetch(
      `${API_URL}/admin/email-history/?${queryParams.toString()}`,
      {
        method: "GET",
        headers: this.getHeaders(true),
      }
    );
    return this.handleResponse<ApiResponse<any>>(response);
  }

  async getEmailHistoryDetail(id: number): Promise<ApiResponse<any>> {
    const response = await fetch(`${API_URL}/admin/email-history/${id}/`, {
      method: "GET",
      headers: this.getHeaders(true),
    });
    return this.handleResponse<ApiResponse<any>>(response);
  }

  // App Settings Management
  async getAppSettings(): Promise<ApiResponse<any>> {
    const response = await fetch(`${API_URL}/admin/app-settings/`, {
      method: "GET",
      headers: this.getHeaders(true),
    });
    return this.handleResponse<ApiResponse<any>>(response);
  }

  async updateAppSettings(data: any): Promise<ApiResponse<any>> {
    const response = await fetch(`${API_URL}/admin/app-settings/`, {
      method: "PUT",
      headers: this.getHeaders(true),
      body: JSON.stringify(data),
    });
    return this.handleResponse<ApiResponse<any>>(response);
  }

  // Weight Templates Management
  async getWeightTemplates(): Promise<ApiResponse<any>> {
    const response = await fetch(`${API_URL}/admin/weight-templates/`, {
      method: "GET",
      headers: this.getHeaders(true),
    });
    return this.handleResponse<ApiResponse<any>>(response);
  }

  async createWeightTemplate(data: any): Promise<ApiResponse<any>> {
    const response = await fetch(`${API_URL}/admin/weight-templates/`, {
      method: "POST",
      headers: this.getHeaders(true),
      body: JSON.stringify(data),
    });
    return this.handleResponse<ApiResponse<any>>(response);
  }

  async updateWeightTemplate(id: number, data: any): Promise<ApiResponse<any>> {
    const response = await fetch(`${API_URL}/admin/weight-templates/${id}/`, {
      method: "PUT",
      headers: this.getHeaders(true),
      body: JSON.stringify(data),
    });
    return this.handleResponse<ApiResponse<any>>(response);
  }

  async deleteWeightTemplate(id: number): Promise<ApiResponse<any>> {
    const response = await fetch(`${API_URL}/admin/weight-templates/${id}/`, {
      method: "DELETE",
      headers: this.getHeaders(true),
    });
    return this.handleResponse<ApiResponse<any>>(response);
  }

  // Carousel Images Management
  async getCarouselImages(): Promise<ApiResponse<any>> {
    const response = await fetch(`${API_URL}/admin/carousel-images/`, {
      method: "GET",
      headers: this.getHeaders(true),
    });
    return this.handleResponse<ApiResponse<any>>(response);
  }

  async uploadCarouselImage(file: File, altText: string = ""): Promise<ApiResponse<any>> {
    const formData = new FormData();
    formData.append('image', file);
    formData.append('alt_text', altText);

    const headers: HeadersInit = {};
    const token = localStorage.getItem("accessToken");
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_URL}/admin/carousel-images/upload/`, {
      method: "POST",
      headers,
      body: formData,
    });
    return this.handleResponse<ApiResponse<any>>(response);
  }

  async deleteCarouselImage(imageId: string): Promise<ApiResponse<any>> {
    const response = await fetch(`${API_URL}/admin/carousel-images/${imageId}/`, {
      method: "DELETE",
      headers: this.getHeaders(true),
    });
    return this.handleResponse<ApiResponse<any>>(response);
  }

  async reorderCarouselImages(imageIds: string[]): Promise<ApiResponse<any>> {
    const response = await fetch(`${API_URL}/admin/carousel-images/reorder/`, {
      method: "PUT",
      headers: this.getHeaders(true),
      body: JSON.stringify({ image_ids: imageIds }),
    });
    return this.handleResponse<ApiResponse<any>>(response);
  }

  // Guest Session Management
  async resetGuestCredits(): Promise<ApiResponse<any>> {
    const response = await fetch(`${API_URL}/admin/guest-sessions/reset-credits/`, {
      method: "POST",
      headers: this.getHeaders(true),
    });
    return this.handleResponse<ApiResponse<any>>(response);
  }

  // Promo Code Management
  async getPromoCodes(): Promise<ApiResponse<any>> {
    const response = await fetch(`${API_URL}/admin/promo-codes/`, {
      method: "GET",
      headers: this.getHeaders(true),
    });
    return this.handleResponse<ApiResponse<any>>(response);
  }

  async createPromoCode(data: any): Promise<ApiResponse<any>> {
    const response = await fetch(`${API_URL}/admin/promo-codes/`, {
      method: "POST",
      headers: this.getHeaders(true),
      body: JSON.stringify(data),
    });
    return this.handleResponse<ApiResponse<any>>(response);
  }

  async updatePromoCode(id: number, data: any): Promise<ApiResponse<any>> {
    const response = await fetch(`${API_URL}/admin/promo-codes/${id}/`, {
      method: "PUT",
      headers: this.getHeaders(true),
      body: JSON.stringify(data),
    });
    return this.handleResponse<ApiResponse<any>>(response);
  }

  async deletePromoCode(id: number): Promise<ApiResponse<any>> {
    const response = await fetch(`${API_URL}/admin/promo-codes/${id}/`, {
      method: "DELETE",
      headers: this.getHeaders(true),
    });
    return this.handleResponse<ApiResponse<any>>(response);
  }

  // CVs Management
  async getCVs(params: {
    page?: number;
    page_size?: number;
    search?: string;
    status?: string;
    user_id?: number | string;
    job_id?: number | string;
    order_by?: string;
  } = {}): Promise<PaginatedApiResponse<any>> {
    const queryParams = new URLSearchParams();
    queryParams.append("page", String(params.page || 1));
    queryParams.append("page_size", String(params.page_size || 20));
    if (params.search) queryParams.append("search", params.search);
    if (params.status && params.status !== "all")
      queryParams.append("status", params.status);
    if (params.user_id && params.user_id !== "all")
      queryParams.append("user_id", String(params.user_id));
    if (params.job_id && params.job_id !== "all")
      queryParams.append("job_id", String(params.job_id));
    if (params.order_by) queryParams.append("order_by", params.order_by);

    return this.makeRequest<PaginatedApiResponse<any>>(
      `${API_URL}/admin/cvs/?${queryParams.toString()}`,
      { headers: this.getHeaders(true) },
      true
    );
  }

  /**
   * Secure CV download with JWT authentication
   * @param cvId - CV ID to download
   * @param preview - Whether to open inline (preview) or download
   * @returns Response object
   */
  async downloadCV(cvId: number, preview: boolean = false): Promise<Response> {
    const endpoint = preview ? '?preview=true' : '';
    const url = `${API_URL}/jobs/cvs/${cvId}/secure-download/${endpoint}`;
    
    const token = localStorage.getItem('accessToken');
    if (!token) {
      throw new Error('Authentication required');
    }
    
    // Add JWT token to headers for authentication
    return fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
  }

  /**
   * Get CV blob URL for download or preview
   * @param cvId - CV ID
   * @param preview - Whether this is for preview
   * @returns Blob URL
   */
  async getCVBlobUrl(cvId: number, preview: boolean = false): Promise<string> {
    const response = await this.downloadCV(cvId, preview);
    
    if (!response.ok) {
      if (response.status === 403) {
        throw new Error('You do not have permission to access this CV');
      } else if (response.status === 404) {
        throw new Error('CV file not found');
      }
      throw new Error('Failed to download CV');
    }
    
    const blob = await response.blob();
    return window.URL.createObjectURL(blob);
  }

  // Maintenance Settings
  async getMaintenanceSettings(): Promise<ApiResponse<any>> {
    const url = `${API_URL}/admin/maintenance-settings/`;
    return this.makeRequest<ApiResponse<any>>(
      url,
      {
        method: "GET",
        headers: this.getHeaders(true),
      },
      true
    );
  }

  async updateMaintenanceSettings(data: any): Promise<ApiResponse<any>> {
    const url = `${API_URL}/admin/maintenance-settings/`;
    return this.makeRequest<ApiResponse<any>>(
      url,
      {
        method: "PUT",
        headers: this.getHeaders(true),
        body: JSON.stringify(data),
      },
      true
    );
  }

  // Invoice/Subscription Management
  async getAllInvoices(params?: {
    search?: string;
    status?: string;
    type?: string;
    start_date?: string;
    end_date?: string;
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<any>> {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.append('search', params.search);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.type) queryParams.append('type', params.type);
    if (params?.start_date) queryParams.append('start_date', params.start_date);
    if (params?.end_date) queryParams.append('end_date', params.end_date);
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.page_size) queryParams.append('page_size', params.page_size.toString());

    const url = `${API_URL}/admin/invoices/?${queryParams.toString()}`;
    return this.makeRequest<PaginatedResponse<any>>(
      url,
      {
        method: "GET",
        headers: this.getHeaders(true),
      },
      true
    );
  }

  async getInvoiceById(id: number): Promise<ApiResponse<any>> {
    const url = `${API_URL}/admin/invoices/${id}/`;
    return this.makeRequest<ApiResponse<any>>(
      url,
      {
        method: "GET",
        headers: this.getHeaders(true),
      },
      true
    );
  }

  async updateInvoice(id: number, data: any): Promise<ApiResponse<any>> {
    const url = `${API_URL}/admin/invoices/${id}/`;
    return this.makeRequest<ApiResponse<any>>(
      url,
      {
        method: "PUT",
        headers: this.getHeaders(true),
        body: JSON.stringify(data),
      },
      true
    );
  }

  async deleteInvoice(id: number): Promise<ApiResponse<any>> {
    const url = `${API_URL}/admin/invoices/${id}/`;
    return this.makeRequest<ApiResponse<any>>(
      url,
      {
        method: "DELETE",
        headers: this.getHeaders(true),
      },
      true
    );
  }

  // ============================================
  // ACTIVITY LOGS
  // ============================================

  async getActivities(params?: {
    page?: number;
    page_size?: number;
    action_type?: string;
    search?: string;
    from_date?: string;
    to_date?: string;
    user_id?: number;
    related_object?: string;
  }): Promise<PaginatedResponse<any>> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.page_size) queryParams.append('page_size', params.page_size.toString());
    if (params?.action_type) queryParams.append('action_type', params.action_type);
    if (params?.search) queryParams.append('search', params.search);
    if (params?.from_date) queryParams.append('from_date', params.from_date);
    if (params?.to_date) queryParams.append('to_date', params.to_date);
    if (params?.user_id) queryParams.append('user_id', params.user_id.toString());
    if (params?.related_object) queryParams.append('related_object', params.related_object);

    const url = `${API_URL}/admin/activities/?${queryParams.toString()}`;
    return this.makeRequest<PaginatedResponse<any>>(
      url,
      {
        method: "GET",
        headers: this.getHeaders(true),
      },
      true
    );
  }

  async getActivityById(id: number): Promise<ApiResponse<any>> {
    const url = `${API_URL}/admin/activities/${id}/`;
    return this.makeRequest<ApiResponse<any>>(
      url,
      {
        method: "GET",
        headers: this.getHeaders(true),
      },
      true
    );
  }

  async getActivityStats(): Promise<ApiResponse<any>> {
    const url = `${API_URL}/admin/activities/stats/`;
    return this.makeRequest<ApiResponse<any>>(
      url,
      {
        method: "GET",
        headers: this.getHeaders(true),
      },
      true
    );
  }

  // ============================================
  // MASTER VARIABLES
  // ============================================

  async getMasterVariables(): Promise<ApiResponse<any>> {
    const url = `${API_URL}/admin/master-variables/`;
    return this.makeRequest<ApiResponse<any>>(
      url,
      {
        method: "GET",
        headers: this.getHeaders(true),
      },
      true
    );
  }

  // A variable writes to exactly one column: `value` for numbers and switches,
  // `string_value` for the text ones. Sending both would store a meaningless
  // 0 alongside a URL.
  async updateMasterVariable(
    id: number,
    data: { value: number } | { string_value: string }
  ): Promise<ApiResponse<any>> {
    const url = `${API_URL}/admin/master-variables/${id}/`;
    return this.makeRequest<ApiResponse<any>>(
      url,
      {
        method: "PUT",
        headers: this.getHeaders(true),
        body: JSON.stringify(data),
      },
      true
    );
  }

  // ============================================
  // GUEST CV MANAGEMENT
  // ============================================

  async getGuestCVs(params: {
    page?: number;
    page_size?: number;
    search?: string;
    date_from?: string;
    date_to?: string;
    extension_id?: string;
    order_by?: string;
  }): Promise<PaginatedApiResponse<any>> {
    const queryParams = new URLSearchParams();
    
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.page_size) queryParams.append('page_size', params.page_size.toString());
    if (params.search) queryParams.append('search', params.search);
    if (params.date_from) queryParams.append('date_from', params.date_from);
    if (params.date_to) queryParams.append('date_to', params.date_to);
    if (params.extension_id) queryParams.append('extension_id', params.extension_id);
    if (params.order_by) queryParams.append('order_by', params.order_by);

    const url = `${API_URL}/admin/guest-cvs/?${queryParams.toString()}`;
    return this.makeRequest<PaginatedApiResponse<any>>(
      url,
      {
        method: "GET",
        headers: this.getHeaders(true),
      },
      true
    );
  }

  async getGuestCVDetail(cvId: number): Promise<ApiResponse<any>> {
    const url = `${API_URL}/admin/guest-cvs/${cvId}/`;
    return this.makeRequest<ApiResponse<any>>(
      url,
      {
        method: "GET",
        headers: this.getHeaders(true),
      },
      true
    );
  }

  // ============================================
  // AI SETTINGS MANAGEMENT
  // ============================================

  async getAIModels(search?: string): Promise<ApiResponse<any[]>> {
    const queryParams = new URLSearchParams();
    if (search) queryParams.append('search', search);

    const url = `${API_URL}/admin/ai-models/?${queryParams.toString()}`;
    return this.makeRequest<ApiResponse<any[]>>(
      url,
      {
        method: "GET",
        headers: this.getHeaders(true),
      },
      true
    );
  }

  async createAIModel(data: {
    provider: string;
    available_models: string[];
    is_active: boolean;
  }): Promise<ApiResponse<any>> {
    const url = `${API_URL}/admin/ai-models/`;
    return this.makeRequest<ApiResponse<any>>(
      url,
      {
        method: "POST",
        headers: this.getHeaders(true),
        body: JSON.stringify(data),
      },
      true
    );
  }

  async updateAIModel(id: number, data: Partial<{
    provider: string;
    available_models: string[];
    is_active: boolean;
  }>): Promise<ApiResponse<any>> {
    const url = `${API_URL}/admin/ai-models/${id}/`;
    return this.makeRequest<ApiResponse<any>>(
      url,
      {
        method: "PUT",
        headers: this.getHeaders(true),
        body: JSON.stringify(data),
      },
      true
    );
  }

  async deleteAIModel(id: number): Promise<ApiResponse<any>> {
    const url = `${API_URL}/admin/ai-models/${id}/`;
    return this.makeRequest<ApiResponse<any>>(
      url,
      {
        method: "DELETE",
        headers: this.getHeaders(true),
      },
      true
    );
  }
}

export const apiService = new ApiService();
